import { Bell, Plus } from "lucide-react";
import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PortalNotificationsList from "src/components/portal-notifications/PortalNotificationsList";
import { SlabButton } from "src/components/ui/SlabButton";
import { actorFromAuth } from "src/lib/activityService";
import { notificationToFormPrefill } from "src/lib/portalNotificationService";
import { canReadService, canWriteService, ServiceDomain } from "src/lib/roles";
import { selectUser, selectUserProfile } from "src/store/authSlice";
import type { PortalNotification } from "src/types/portalNotifications";
import { Constants } from "../constant";

const PortalNotificationsPanel: React.FC = () => {
  const user = useSelector(selectUser);
  const userProfile = useSelector(selectUserProfile);
  const navigate = useNavigate();

  const canWrite =
    userProfile != null &&
    canWriteService(userProfile.role, ServiceDomain.PortalNotifications);
  const actor = actorFromAuth(user, userProfile);

  const handleEdit = (n: PortalNotification) => {
    navigate(`${Constants.PORTAL_NOTIFICATIONS_ROUTE}/compose`, {
      state: { prefill: notificationToFormPrefill(n), notificationId: n.id },
    });
  };

  const handleDuplicate = (n: PortalNotification) => {
    // Duplicate always creates a NEW draft — never reuses the source's id.
    navigate(`${Constants.PORTAL_NOTIFICATIONS_ROUTE}/compose`, {
      state: { prefill: notificationToFormPrefill(n) },
    });
  };

  if (
    !userProfile ||
    !canReadService(userProfile.role, ServiceDomain.PortalNotifications)
  ) {
    return (
      <div className="py-6 sm:py-8">
        <h1 className="mb-3 text-2xl font-bold sm:mb-4 sm:text-4xl">
          Access Denied
        </h1>
        <p className="text-sm text-gray-400 sm:text-base">
          You don&apos;t have permission to view portal notifications.
        </p>
      </div>
    );
  }

  return (
    // 3rem matches AppLayout main vertical padding (py-6), so this panel
    // fills the viewport and only the notification list scrolls.
    <div className="mx-auto flex h-[calc(100dvh-3rem)] min-h-0 w-full max-w-6xl flex-col overflow-hidden py-2 sm:py-4">
      <header className="mb-6 flex shrink-0 flex-col gap-4 sm:mb-8 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="mb-2 flex items-center gap-2 text-2xl font-bold sm:gap-3 sm:text-4xl">
            <Bell className="h-7 w-7 shrink-0 text-teal-500 sm:h-8 sm:w-8" />
            Portal notifications
          </h1>
          <p className="max-w-2xl text-sm text-[#cfd1d7] sm:text-base">
            Author login pop-up notifications for the Client Portal. Published
            notifications also live in the Notification Hub for 30 days.
          </p>
        </div>
        {canWrite && (
          <SlabButton
            variant="primary"
            onClick={() =>
              navigate(`${Constants.PORTAL_NOTIFICATIONS_ROUTE}/compose`)
            }
            className="w-full sm:w-auto"
          >
            <Plus className="h-4 w-4" aria-hidden /> New notification
          </SlabButton>
        )}
      </header>

      <PortalNotificationsList
        canWrite={canWrite}
        actor={actor}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
      />
    </div>
  );
};

export default PortalNotificationsPanel;
